import {
  CHANNEL_LABELS,
  LIVE_CHANNELS,
  type BroadSector,
  type Channel,
} from '../../shared/alerts/rule';
import {
  MOMENTS,
  SIGNUP_TICKETS_EUR,
  type Moment,
  type SignupChannel,
  type SignupRule,
  type SignupTicket,
} from '../../shared/alerts/signup-rule';
import {
  PROVIDERS as SHARED_PROVIDERS,
  providerLabel,
  type Provider,
} from '../../shared/auth/providers';
import { countryName } from '../../shared/feed/countries';
import {
  FX,
  REGIONS,
  SECTORS,
  platformById,
  type Region,
  type SampleDeal,
} from './data';
import { NEAR_CAP, canInvest, deco, type Urgency } from './deco';
import { cd, cdShort, fD, fDT, fT, money, pct } from './format';
import { SIGNUP_PATH } from './platforms';
import { ticketUsd } from './rules';

/**
 * /get-started's rule builder: the rule a visitor builds in four steps, the
 * sentence it reads as, and the alerts it would have sent over the last 30
 * days of sample raises. Pure, so the server's first paint, the browser and
 * the tests agree.
 */

/** "Minimum ticket": 0 is "Any". */
export type Ticket = 0 | SignupTicket;
export const TICKETS: Ticket[] = [0, ...SIGNUP_TICKETS_EUR];

export interface Answers {
  /**
   * Where the visitor lives, an ISO code: the one they picked, or else the
   * guess (Stored.countryChosen says which).
   */
  country: string;
  /** "Only raises open to me". */
  onlyEligible: boolean;
  /** In the order picked. Empty: every region. */
  regions: Region[];
  /** In the order picked. Empty: every sector. */
  sectors: string[];
  ticket: Ticket;
  moments: Moment[];
  channels: Channel[];
}

/** Where the visitor lives until their address or time zone says. */
export const DEFAULT_COUNTRY = 'GB';

export const DEFAULT_ANSWERS: Answers = {
  country: DEFAULT_COUNTRY,
  onlyEligible: false,
  regions: ['UK', 'EU', 'US'],
  sectors: ['SaaS', 'Fintech', 'Healthtech'],
  ticket: 0,
  moments: ['launch', '7d', '72h', 'near_cap'],
  channels: ['EMAIL'],
};

/** The steps, as the sentence's tokens point at them. */
export const STEP = { where: 0, what: 1, when: 2, signUp: 3 } as const;
export const STEP_COUNT = 4;

export const TICKET_LABELS: Record<Ticket, string> = {
  0: 'Any',
  100: '≤ €100',
  500: '≤ €500',
  1000: '≤ €1,000',
};

export const MOMENT_LABELS: Record<Moment, string> = {
  launch: 'At launch',
  '7d': '7 days left',
  '72h': '72 hours left',
  '24h': '24 hours left',
  near_cap: 'Near cap',
  perk_expiry: 'Perk ending',
};

/**
 * The channels the page offers: the ones that deliver today. Telegram, push
 * and RSS stay off the page until they do (design/README.md, "email only
 * at launch").
 */
export const SITE_CHANNELS: readonly Channel[] = LIVE_CHANNELS;

/** A country the picker lists: an ISO code Intl has a name for. */
export function isCountry(code: unknown): code is string {
  return (
    typeof code === 'string' &&
    /^[A-Z]{2}$/.test(code) &&
    countryName(code) !== code
  );
}

// ---- What this browser keeps ----------------------------------------------------

/**
 * The answers as this browser keeps them. `countryChosen` says the visitor
 * picked the country, so the page's guess at where they are leaves it be.
 */
export interface Stored {
  answers: Answers;
  countryChosen: boolean;
}

/**
 * Answers read back from storage, or null if they are not answers. A field
 * that is missing or no longer valid takes its default, so answers kept by
 * the old questionnaire still open the builder.
 */
export function parseStored(raw: unknown): Stored | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const d = DEFAULT_ANSWERS;
  const list = <T extends string>(
    v: unknown,
    allowed: readonly T[],
    fallback: T[],
  ): T[] =>
    Array.isArray(v)
      ? [...new Set(v.filter((x): x is T => allowed.includes(x as T)))]
      : [...fallback];
  const country = isCountry(r.country) ? r.country : d.country;
  return {
    answers: {
      country,
      onlyEligible:
        typeof r.onlyEligible === 'boolean' ? r.onlyEligible : d.onlyEligible,
      regions: list(r.regions, REGIONS, d.regions),
      sectors: list(r.sectors, SECTORS, d.sectors),
      ticket: TICKETS.includes(r.ticket as Ticket)
        ? (r.ticket as Ticket)
        : d.ticket,
      moments: list(r.moments, MOMENTS, d.moments),
      channels: list(r.channels, SITE_CHANNELS, d.channels),
    },
    countryChosen: r.countryChosen === true && country === r.country,
  };
}

// ---- The sentence -----------------------------------------------------------------

/** "A", "A and B", "A, B and C". */
export function naturalList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const CLOSING_WORDS = { '7d': '7 days', '72h': '72 hours', '24h': '24 hours' };

/** "at launch, 7 days and 72 hours before close and at 90% of cap". */
export function momentsPhrase(moments: readonly Moment[]): string {
  const has = (m: Moment) => moments.includes(m);
  const parts: string[] = [];
  if (has('launch')) parts.push('at launch');
  const closing = (['7d', '72h', '24h'] as const)
    .filter(has)
    .map((m) => CLOSING_WORDS[m]);
  if (closing.length) parts.push(`${naturalList(closing)} before close`);
  if (has('near_cap')) parts.push('at 90% of cap');
  if (has('perk_expiry')) parts.push('before a perk ends');
  return parts.length ? naturalList(parts) : 'in the Weekly Sonar only';
}

/** A run of the sentence: plain text, or a token naming the step that sets it. */
export interface SentencePart {
  text: string;
  /** The step this token is set on; null for the words between tokens. */
  step: number | null;
}

/**
 * "Alert me about SaaS, Fintech and Healthtech raises in UK + EU + US, at
 * launch, … by Email." The optional clauses appear only when set.
 */
export function sentence(a: Answers): SentencePart[] {
  const words = (text: string): SentencePart => ({ text, step: null });
  const token = (text: string, step: number): SentencePart => ({ text, step });
  const parts = [
    words('Alert me about '),
    token(a.sectors.length ? naturalList(a.sectors) : 'all', STEP.what),
    words(' raises in '),
    token(
      a.regions.length ? a.regions.join(' + ') : 'every region',
      STEP.where,
    ),
  ];
  if (a.onlyEligible) {
    parts.push(
      words(' that are open to '),
      token(`${countryName(a.country)} residents`, STEP.where),
    );
  }
  if (a.ticket) {
    parts.push(
      words(' with a minimum ticket '),
      token(TICKET_LABELS[a.ticket], STEP.what),
    );
  }
  parts.push(
    words(', '),
    token(momentsPhrase(a.moments), STEP.when),
    words(', by '),
    token(
      a.channels.length
        ? a.channels.map((c) => CHANNEL_LABELS[c]).join(' + ')
        : CHANNEL_LABELS.EMAIL,
      STEP.when,
    ),
    words('.'),
  );
  return parts;
}

// ---- What it matches --------------------------------------------------------------

/**
 * Whether the rule takes this raise. An empty region or sector list means
 * every one, as the sentence says; tickets compare in dollars, at the
 * sample's euro rate.
 */
export function matches(a: Answers, deal: SampleDeal): boolean {
  return (
    (a.regions.length === 0 || a.regions.includes(deal.region)) &&
    (a.sectors.length === 0 || a.sectors.includes(deal.sector)) &&
    (a.ticket === 0 || ticketUsd(deal) <= a.ticket / FX.EUR + 1e-9) &&
    (!a.onlyEligible || canInvest(deal, a.country))
  );
}

// ---- The alerts it sends ---------------------------------------------------------

const H = 3_600_000;
/** How far back the preview replays. */
export const WINDOW_MS = 30 * 24 * H;
/** Rows the list shows before "+ n more". */
export const LIST_ROWS = 8;

export type AlertTone = 'accent' | 'urgent' | 'critical' | 'cap';

export interface AlertEvent {
  /** One per raise and moment. */
  key: string;
  at: number;
  moment: Moment;
  deal: SampleDeal;
  /** "New raise", "72 hours left", "Near cap · 99.9%". */
  kind: string;
  glyph: '○' | '▲' | '■' | '◆';
  tone: AlertTone;
}

/** Hours before the close each closing reminder fires. */
const CLOSING_HOURS = { '7d': 168, '72h': 72, '24h': 24 } as const;
/** A perk's alert goes this long before it ends. */
const PERK_LEAD_MS = 48 * H;

function eventFor(moment: Moment, deal: SampleDeal): AlertEvent | null {
  const make = (
    at: number,
    kind: string,
    glyph: AlertEvent['glyph'],
    tone: AlertTone,
  ): AlertEvent => ({
    key: `${deal.id}:${moment}`,
    at,
    moment,
    deal,
    kind,
    glyph,
    tone,
  });
  switch (moment) {
    case 'launch':
      return make(deal.launchAt, 'New raise', '○', 'accent');
    case '7d':
    case '72h':
    case '24h': {
      const at = deal.closesAt - CLOSING_HOURS[moment] * H;
      return moment === '24h'
        ? make(at, MOMENT_LABELS[moment], '■', 'critical')
        : make(at, MOMENT_LABELS[moment], '▲', 'urgent');
    }
    case 'near_cap': {
      // When the raise was seen past 90%: its last check.
      if (deal.max === null || deal.raised === null) return null;
      const ratio = deal.raised / deal.max;
      if (ratio < NEAR_CAP) return null;
      return make(deal.checkedAt, `Near cap · ${pct(ratio * 100)}`, '◆', 'cap');
    }
    case 'perk_expiry':
      if (!deal.perk) return null;
      return make(
        deal.perk.endsAt - PERK_LEAD_MS,
        `${deal.perk.text} ending`,
        '■',
        'critical',
      );
  }
}

/**
 * Every alert the rule sends over the raises' lives: one per matching raise
 * and chosen moment, kept only while the raise runs, from its launch to its
 * close.
 */
export function alertEvents(a: Answers, deals: SampleDeal[]): AlertEvent[] {
  const out: AlertEvent[] = [];
  for (const deal of deals) {
    if (!matches(a, deal)) continue;
    for (const moment of MOMENTS) {
      if (!a.moments.includes(moment)) continue;
      const e = eventFor(moment, deal);
      if (e && e.at >= deal.launchAt && e.at < deal.closesAt) out.push(e);
    }
  }
  return out;
}

/** What it sent in the last 30 days up to now, newest first. */
export function pastAlerts(events: AlertEvent[], now: number): AlertEvent[] {
  return events
    .filter((e) => e.at <= now && e.at > now - WINDOW_MS)
    .sort((x, y) => y.at - x.at || x.key.localeCompare(y.key));
}

/** The next one it will send, or null. */
export function nextAlert(
  events: AlertEvent[],
  now: number,
): AlertEvent | null {
  let next: AlertEvent | null = null;
  for (const e of events) {
    if (e.at > now && (!next || e.at < next.at)) next = e;
  }
  return next;
}

// ---- The preview ------------------------------------------------------------------

/** "✓ Open to foreign investors", "✕ Not open to Germany residents". */
export function eligibilityOf(
  deal: SampleDeal,
  country: string,
): { eligible: boolean; text: string } {
  const eligible = canInvest(deal, country);
  if (!eligible) {
    return {
      eligible,
      text: `✕ Not open to ${countryName(country)} residents`,
    };
  }
  return {
    eligible,
    text:
      deal.openTo === 'foreign'
        ? '✓ Open to foreign investors'
        : `✓ Open to ${countryName(country)} residents`,
  };
}

export interface AlertRow {
  key: string;
  glyph: string;
  tone: AlertTone;
  deal: string;
  kind: string;
  /** "Thu 24 Sep · 16:20 BST". */
  when: string;
  /** "Wefunder · Reg CF · SaaS", and who cannot invest. */
  sub: string;
  eligible: boolean;
  selected: boolean;
}

export interface FeaturedAlert {
  key: string;
  /** "Raise Sonar · Email". */
  sender: string;
  sent: string;
  /** "▲ 72 HOURS LEFT". */
  kicker: string;
  tone: AlertTone;
  title: string;
  line: string;
  urgency: Urgency;
  /** "■ Closes Sun 27 Sep". */
  closes: string;
  countdown: string;
  /** What a screen reader hears for the countdown: "Closes in 2 days, 6 hours". */
  spoken: string;
  eligible: boolean;
  eligibility: string;
  provenance: string;
}

export interface Preview {
  /** "Last 30 days · 10:42 BST". */
  clock: string;
  count: string;
  label: string;
  empty: boolean;
  featured: FeaturedAlert | null;
  rows: AlertRow[];
  /** "+ 4 more", or null when every alert is listed. */
  more: string | null;
  next: { label: string; text: string } | null;
}

/** The sentence beside the count. */
export function headline(count: number, raises: number): string {
  if (count === 0) {
    return 'alerts this rule would have sent you in the last 30 days.';
  }
  return `${count === 1 ? 'alert' : 'alerts'} this rule would have sent you in the last 30 days, about ${raises} ${raises === 1 ? 'raise' : 'raises'}.`;
}

function featured(e: AlertEvent, a: Answers, now: number): FeaturedAlert {
  const { deal } = e;
  const d = deco(deal, now);
  const channel = a.channels[0] ?? 'EMAIL';
  const who = eligibilityOf(deal, a.country);
  return {
    key: e.key,
    sender: `Raise Sonar · ${CHANNEL_LABELS[channel]}`,
    sent: fDT(e.at),
    kicker: `${e.glyph} ${e.kind.toUpperCase()}`,
    tone: e.tone,
    title: `${deal.name} on ${platformById(deal.platform).name}`,
    line: [
      deal.regime,
      deal.terms,
      deal.raised === null
        ? null
        : `${money(deal.raised, deal.currency)} raised`,
    ]
      .filter(Boolean)
      .join(' · '),
    urgency: d.urgency,
    closes: `${d.urgencyGlyph} Closes ${d.closeShort}`,
    countdown: cd(d.msLeft),
    spoken: d.spoken,
    eligible: who.eligible,
    eligibility: who.text,
    provenance: `↳ ${d.provenance}`,
  };
}

function row(e: AlertEvent, a: Answers, selected: boolean): AlertRow {
  const { deal } = e;
  const eligible = canInvest(deal, a.country);
  const sub = [platformById(deal.platform).name, deal.regime, deal.sector].join(
    ' · ',
  );
  return {
    key: e.key,
    glyph: e.glyph,
    tone: e.tone,
    deal: deal.name,
    kind: e.kind,
    when: `${fD(e.at)} · ${fT(e.at)}`,
    sub: eligible
      ? sub
      : `${sub} · not open to ${countryName(a.country)} residents`,
    eligible,
    selected,
  };
}

/**
 * The preview card at one instant. `selected` is the alert the visitor
 * picked from the list; the newest stands in when it is gone or unset.
 */
export function preview(
  a: Answers,
  deals: SampleDeal[],
  now: number,
  selected: string | null = null,
): Preview {
  const events = alertEvents(a, deals);
  const past = pastAlerts(events, now);
  const chosen = past.find((e) => e.key === selected) ?? past[0] ?? null;
  const raises = new Set(past.map((e) => e.deal.id)).size;
  const next = nextAlert(events, now);
  return {
    clock: `Last 30 days · ${fT(now)}`,
    count: String(past.length),
    label: headline(past.length, raises),
    empty: past.length === 0,
    featured: chosen && featured(chosen, a, now),
    rows: past.slice(0, LIST_ROWS).map((e) => row(e, a, e.key === chosen?.key)),
    more: past.length > LIST_ROWS ? `+ ${past.length - LIST_ROWS} more` : null,
    next: next && {
      label: `NEXT · IN ${cdShort(next.at - now).toUpperCase()}`,
      text: `${next.kind} · ${next.deal.name} on ${platformById(next.deal.platform).name}`,
    },
  };
}

// ---- What sign-up sends ----------------------------------------------------------

/** Every signup from this page says so, for the dashboard and its events. */
export const SIGNUP_FROM = 'get-started';

/**
 * Where an account goes from here: the Alerts page, where the saved rule
 * waits for the trial. web/src/auth/next.ts sends a new account there
 * anyway; naming it keeps a visitor who signs in to an account they had
 * on the same way. Written for a dashboard under /app/; where it has a host
 * of its own, the sign-in page's redirect drops the prefix on the way
 * (src/auth/app-url.ts, dashboardDestination).
 */
export const AFTER_SIGNUP = '/app/alerts';

export type SignupProvider = Provider;

/** Step 4's other ways in, as the sign-up page labels them. */
export const PROVIDERS: { id: SignupProvider; label: string }[] =
  SHARED_PROVIDERS.map((id) => ({ id, label: providerLabel(id, false) }));

/** Where an account goes once in, with the rule for the dashboard to save. */
export function afterSignup(rule: SignupRule | null): string {
  return rule
    ? `${AFTER_SIGNUP}?rule=${encodeURIComponent(JSON.stringify(rule))}`
    : AFTER_SIGNUP;
}

/** The sign-in page's OAuth return (web/src/auth), on this site's origin. */
export const SSO_CALLBACK_PATH = '/sso-callback';

/**
 * Where Google or Apple, started from this page, come back to: the sign-in
 * page's own OAuth return, in its own terms (`next`, `via`: callbackSearch
 * in web/src/auth/callback.ts), which finishes the sign-up whichever page
 * began it and goes on to `next`. And that destination as a full address,
 * which Clerk also asks for.
 */
export function oauthReturn(
  provider: SignupProvider,
  rule: SignupRule | null,
  origin: string,
): { callbackUrl: string; destinationUrl: string } {
  const next = afterSignup(rule);
  const query = new URLSearchParams({ next, via: provider });
  return {
    callbackUrl: `${origin}${SSO_CALLBACK_PATH}?${query.toString()}`,
    destinationUrl: `${origin}${next}`,
  };
}

/**
 * The provider links' own address: the sign-up page, which starts that
 * provider at once (`provider=`). The script goes to the provider directly
 * (client/clerk-oauth.ts); this is where a new tab, the page without its
 * script, or Clerk failing to load on this page goes instead. The rule rides
 * along in where the page goes afterwards; null sends none, as the page
 * drawn without its script has none of the visitor's.
 */
export function providerHref(
  provider: SignupProvider,
  rule: SignupRule | null,
): string {
  const params = new URLSearchParams({
    from: SIGNUP_FROM,
    provider,
    redirect_url: afterSignup(rule),
  });
  return `${SIGNUP_PATH}?${params.toString()}`;
}

/** "Already have an account? Sign in", to the same place. */
export const SIGN_IN_HREF = `/login?redirect_url=${encodeURIComponent(AFTER_SIGNUP)}`;

/**
 * The page's own check before Clerk is asked: the server's signup pattern
 * (src/subscriber/subscriber.controller.ts), deliberately permissive. The
 * sign-in page's stricter one runs after it (src/shared/auth/email.ts).
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const EMAIL_ERROR =
  'Add your email so we know where to send the first alert.';

/**
 * The rule as the dashboard sends it on (web/src/handoff.ts). A country the visitor
 * did not pick is the page's guess, sent as null so nothing saves it, as
 * the dashboard's picker saves no guess.
 */
export function signupRule(a: Answers, countryChosen: boolean): SignupRule {
  return {
    country: countryChosen ? a.country : null,
    onlyEligible: a.onlyEligible,
    regions: [...a.regions],
    sectors: a.sectors as BroadSector[],
    maxMinTicketEur: a.ticket === 0 ? null : a.ticket,
    moments: MOMENTS.filter((m) => a.moments.includes(m)),
    channels: a.channels.map((c) => c.toLowerCase() as SignupChannel),
  };
}
